import { useMemo, useState } from 'react';

import type { MediaQueryOptions, sizeOfMediaQuery } from '@/hooks/media-query';

import { useIsomorphicLayoutEffect } from '@/hooks/isomorphi-layout-effect';

const IS_SERVER = typeof window === 'undefined';

/// Tailwindcss breakpoints - https://tailwindcss.com/docs/responsive-design
export const MediaQuerySizes = {
  sm: '640px', // 40rem
  md: '768px', // 48rem
  lg: '1024px', // 64rem
  xl: '1280px', // 80rem
  '2xl': '1536px', // 96rem
} as const;

/**
 * React hook for CSS media queries (Tailwind breakpoints).
 * @param size - Breakpoint key.
 * @param options - Optional config.
 * @returns Boolean if query matches.
 * @example
 * const isLarge = useMediaQuery('lg');
 */
export function useMediaQuery(queryOptionSize: sizeOfMediaQuery, { defaultValue = false, initializeWithValue = true }: MediaQueryOptions = {}): boolean {
  const query = useMemo(() => {
    return `(min-width: ${MediaQuerySizes[queryOptionSize]})`;
  }, [queryOptionSize]);

  const getMatches = (query: string): boolean => {
    if (IS_SERVER) {
      return defaultValue;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean>(() => {
    if (initializeWithValue) {
      return getMatches(query);
    }
    return defaultValue;
  });

  /**
   * Handles the change event of the media query.
   * Updates the state when the media query match status changes.
   */
  function handleChange() {
    setMatches(getMatches(query));
  }

  useIsomorphicLayoutEffect(() => {
    const matchMedia = window.matchMedia(query);

    handleChange();

    matchMedia.addEventListener('change', handleChange);

    return () => {
      matchMedia.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}
